// Materials & labour table — extracted from QuoteBuilder for clarity.
import React from "react";
import { Trash, DotsSixVertical } from "@phosphor-icons/react";
import { POUND } from "../../lib/api";
import { MaterialThumb } from "../../lib/materialThumbs";

export default function MaterialsTable({
  quote,
  materials,
  preview,
  dragIdx,
  setDragIdx,
  reorderMaterial,
  removeMaterial,
  updateMaterialRow,
  addMaterialFromCatalog,
  setField,
  onBack,
  onNext,
}) {
  return (
    <div className="card-elev p-5" data-testid="step-materials">
      <div className="overline">Step 4</div>
      <h3 className="font-display text-2xl font-bold mt-1">Materials, Labour &amp; Margins</h3>

      <div className="mt-5">
        <div className="overline mb-2">Line items</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left">
                <th className="overline p-2 w-6"></th>
                <th className="overline p-2 w-14"></th>
                <th className="overline p-2">Material</th>
                <th className="overline p-2 text-right">Qty</th>
                <th className="overline p-2">Unit</th>
                <th className="overline p-2 text-right">Price</th>
                <th className="overline p-2 text-right">Total</th>
                <th></th>
              </tr>
            </thead>
            <tbody className="font-mono-data">
              {quote.materials.length === 0 && (
                <tr><td colSpan={8} className="p-3 text-center font-sans text-slate-500">Add materials from the catalogue below.</td></tr>
              )}
              {quote.materials.map((m, i) => (
                <tr
                  key={m._uid || `idx-${i}`}
                  className={`border-b border-slate-100 ${dragIdx === i ? "opacity-40" : ""}`}
                  data-testid={`material-line-${i}`}
                  draggable
                  onDragStart={() => setDragIdx(i)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => { reorderMaterial(dragIdx, i); setDragIdx(null); }}
                  onDragEnd={() => setDragIdx(null)}
                >
                  <td className="p-2 cursor-grab text-slate-400 hover:text-slate-700" data-testid={`material-drag-${i}`} title="Drag to reorder"><DotsSixVertical size={16} weight="bold" /></td>
                  <td className="p-2"><MaterialThumb category={m.category || ""} name={m.name || ""} /></td>
                  <td className="p-2 font-sans">{m.name}<div className="text-[10px] text-slate-500 uppercase">{m.manufacturer}</div></td>
                  <td className="p-2 text-right"><input type="number" className="rqp text-right !py-1" style={{ width: 80 }} value={m.quantity} onChange={(e) => updateMaterialRow(i, "quantity", e.target.value)} data-testid={`material-qty-${i}`} /></td>
                  <td className="p-2 text-xs">/ {m.unit}</td>
                  <td className="p-2 text-right"><input type="number" step="0.01" className="rqp text-right !py-1" style={{ width: 90 }} value={m.price} onChange={(e) => updateMaterialRow(i, "price", e.target.value)} /></td>
                  <td className="p-2 text-right font-semibold">{POUND((Number(m.quantity) || 0) * (Number(m.price) || 0))}</td>
                  <td className="p-2 text-right"><button onClick={() => removeMaterial(i)} className="btn-secondary !px-2 !py-1 text-red-600" data-testid={`material-remove-${i}`}><Trash size={12} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-4">
        <div className="overline mb-2">Add from catalogue (qty pre-filled from total roof area)</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-72 overflow-y-auto pr-1">
          {materials.slice(0, 60).map((mat) => (
            <button key={mat.id} onClick={() => addMaterialFromCatalog(mat)} className="text-left border border-slate-200 hover:border-[var(--rqp-orange)] p-2 rounded-sm flex gap-2 items-center" data-testid={`catalog-add-${mat.id}`}>
              <MaterialThumb category={mat.category} name={mat.name} />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate">{mat.name}</div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500">{mat.manufacturer} · {mat.category}</div>
              </div>
              <div className="text-right shrink-0">
                <div className="font-mono-data text-sm font-bold">{POUND(mat.price)}</div>
                <div className="text-[10px] text-slate-500">/{mat.unit}</div>
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6 pt-5 border-t border-slate-200">
        <label className="block"><span className="overline">Labour hours</span><input type="number" className="rqp mt-1.5" value={quote.labour_hours} onChange={(e) => setField("labour_hours", e.target.value)} data-testid="labour-hours-input" /></label>
        <label className="block"><span className="overline">Rate (£/h)</span><input type="number" className="rqp mt-1.5" value={quote.labour_rate} onChange={(e) => setField("labour_rate", e.target.value)} data-testid="labour-rate-input" /></label>
        <label className="block"><span className="overline">Scaffolding (£)</span><input type="number" className="rqp mt-1.5" value={quote.scaffolding} onChange={(e) => setField("scaffolding", e.target.value)} data-testid="scaffolding-input" /></label>
        <label className="block"><span className="overline">Waste removal (£)</span><input type="number" className="rqp mt-1.5" value={quote.waste_removal} onChange={(e) => setField("waste_removal", e.target.value)} /></label>
        <label className="block"><span className="overline">Overheads %</span><input type="number" className="rqp mt-1.5" value={quote.overheads_pct} onChange={(e) => setField("overheads_pct", e.target.value)} /></label>
        <label className="block"><span className="overline">Profit margin %</span><input type="number" className="rqp mt-1.5" value={quote.profit_margin_pct} onChange={(e) => setField("profit_margin_pct", e.target.value)} data-testid="profit-margin-input" /></label>
        <label className="block">
          <span className="overline">VAT %</span>
          <input type="number" className="rqp mt-1.5" value={quote.vat_pct} onChange={(e) => setField("vat_pct", e.target.value)} disabled={quote.vat_enabled === false} />
        </label>
        <label className="flex items-center gap-2 mt-7 select-none cursor-pointer" data-testid="vat-toggle-label">
          <input
            type="checkbox"
            checked={quote.vat_enabled !== false}
            onChange={(e) => setField("vat_enabled", e.target.checked)}
            className="w-4 h-4 accent-[var(--rqp-orange)]"
            data-testid="vat-toggle"
          />
          <span className="text-xs uppercase tracking-wider font-semibold">VAT registered</span>
        </label>
      </div>

      {/* Deposit % — drives the customer-viewer Stripe checkout */}
      <div className="mt-5 pt-5 border-t border-slate-200">
        <div className="overline">Customer deposit</div>
        <div className="flex items-center gap-3 mt-2">
          <input
            type="range"
            min="0"
            max="100"
            step="5"
            value={quote.deposit_pct ?? 25}
            onChange={(e) => setField("deposit_pct", Number(e.target.value))}
            className="flex-1 accent-[var(--rqp-orange)]"
            data-testid="deposit-pct-slider"
          />
          <input
            type="number"
            min="0"
            max="100"
            step="1"
            value={quote.deposit_pct ?? 25}
            onChange={(e) => setField("deposit_pct", Math.max(0, Math.min(100, Number(e.target.value) || 0)))}
            className="rqp w-20 text-right"
            data-testid="deposit-pct-input"
          />
          <span className="text-xs font-mono-data font-bold">%</span>
        </div>
        <div className="text-[11px] text-slate-500 mt-2">
          Customer pays this percentage upfront via Stripe when accepting the quote. Set to 0 to disable deposit.
        </div>
      </div>

      <div className="mt-5 flex justify-between">
        <button className="btn-secondary" onClick={onBack}>← Back</button>
        <button className="btn-primary" onClick={onNext} data-testid="step-4-next">Continue to review →</button>
      </div>
    </div>
  );
}
